// ante-react-kit/src/components/AntQuickPopup/helpers/QPField.js
import React, { useMemo } from 'react';
import { useQuickPopupContext } from '../QuickPopupContext';
import fieldRenderer from '../utils/fieldRenderer';
import AntLocalization from '../../../utils/AntLocalization';
import AntCondition from '../../../utils/AntCondition';
import './QPField.scss';

export default function QPField(props) {
    const {
        name,
        editorName,
        editorType,
        label,
        placeholder,
        format,
        alignment,
        bold = false,
        colSpan = 1,
        visible,
        visibleSelf,
        disable,
        required,
        onValueChanged
    } = props;

    const ctx = useQuickPopupContext();
    const ctxData = ctx && ctx.data;

    // Visible check (Condition system) — all hooks must run unconditionally
    const isVisible = useMemo(() => {
        if (!ctxData) return false;
        if (visible && ctx && !ctx.checkVisible(visible)) return false;
        if (visibleSelf && !AntCondition.isValid(ctxData, visibleSelf)) return false;
        return true;
    }, [visible, visibleSelf, ctxData, ctx]);

    // Resolve editor
    const editor = useMemo(
        () => (ctx ? ctx.resolveEditor(editorName, editorType) : null),
        [editorName, editorType, ctx]
    );

    // Resolve lookup
    const lookupValues = useMemo(() => (ctx ? ctx.resolveLookup(name) : []), [name, ctx]);

    // Resolve label
    const resolvedLabel = useMemo(() => {
        if (label === false) return null;
        if (label) return AntLocalization.translateText(label) || label;
        const fc = (ctx && ctx.fieldsCnf || []).find((f) => f.dataField === name);
        if (fc && fc.label) return AntLocalization.translateText(fc.label);
        return name;
    }, [label, name, ctx]);

    const fieldCnf = useMemo(
        () => ((ctx && ctx.fieldsCnf) || []).find((f) => f.dataField === name),
        [ctx, name]
    );

    if (!ctxData || !isVisible) return null;

    // Editable check (BE edit config + disable condition)
    const editable = ctx.canEditField(name);
    const disabledByCondition = disable && AntCondition.isValid(ctxData, disable);
    const disabled = !editable || disabledByCondition;

    const value = ctxData[name];

    const handleChange = (newValue) => {
        ctx.setField(name, newValue);
        if (onValueChanged) onValueChanged(newValue, value);
    };

    return (
        <div className={'qp-field qp-cs-' + colSpan + (disabled ? ' qp-disabled' : '')}>
            {resolvedLabel != null && (
                <label className="qp-label">
                    {resolvedLabel}
                    {required && <span className="qp-required">*</span>}
                </label>
            )}
            <div className={'qp-input' + (alignment ? ' qp-align-' + alignment : '') + (bold ? ' qp-bold' : '')}>
                {fieldRenderer({
                    editor,
                    value,
                    onValueChanged: handleChange,
                    disabled,
                    lookupValues,
                    fieldCnf: { ...fieldCnf, format: format || (fieldCnf && fieldCnf.format) },
                    placeholder
                })}
            </div>
        </div>
    );
}
